/**
 * Signing in with a messenger on the web: SoulThread's connect service links a session for the
 * person by QR code, then keeps their messages in sync without the Mac app.
 */
export const CLOUD_PROVIDERS = ['telegram', 'whatsapp', 'signal'] as const;
export type CloudProvider = (typeof CLOUD_PROVIDERS)[number];

export const CLOUD_PROVIDER_NAMES: Record<CloudProvider, string> = {
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
  signal: 'Signal',
};

/** No messenger can be signed in with, as when the connect service isn't reachable. */
export const NO_CLOUD_PROVIDERS = Object.fromEntries(
  CLOUD_PROVIDERS.map((provider) => [provider, false])
) as Record<CloudProvider, boolean>;

/** The order messengers are shown in when the region is unknown: the one most people have first. */
export const DEFAULT_MESSENGER_ORDER: CloudProvider[] = ['whatsapp', 'signal', 'telegram'];

/**
 * The messengers to feature first in a country (ISO code), before the rest: each region's dominant
 * apps. A region messenger only shows once its connector is offered, so listing one here is safe
 * before it exists. Countries not listed fall back to `DEFAULT_MESSENGER_ORDER`.
 */
export const REGION_MESSENGER_PRIORITY: Partial<Record<string, CloudProvider[]>> = {
  // Telegram-first regions.
  RU: ['telegram'],
  BY: ['telegram'],
  UZ: ['telegram'],
  KZ: ['telegram'],
  KG: ['telegram'],
  TJ: ['telegram'],
  IR: ['telegram'],
  UA: ['telegram'],
  ET: ['telegram'],
};

/**
 * The messengers a visitor can sign in with, most relevant to their region first. `available` is
 * the set offered right now; `country` is the visitor's ISO code, if known. Featured-and-available
 * messengers lead, in the region's order, then the rest in the default order.
 */
export function orderMessengersForRegion(
  available: readonly CloudProvider[],
  country?: string | null
): CloudProvider[] {
  const featured = (country && REGION_MESSENGER_PRIORITY[country.toUpperCase()]) || [];
  const order = [...featured, ...DEFAULT_MESSENGER_ORDER];
  const seen = new Set<CloudProvider>();
  return order.filter(
    (provider) => available.includes(provider) && !seen.has(provider) && seen.add(provider)
  );
}

/** The Terms someone agrees to on the connect screen: the "Last updated" date on /terms. */
export const TERMS_VERSION = '2026-09-28';

/** Where a sign-in with a messenger stands, as the browser that started it sees it. */
export type ConnectLoginView =
  | { state: 'starting' }
  | { state: 'qr'; link: string }
  /** The messenger account has a password of its own (Telegram's two-step verification). */
  | { state: 'password'; hint: string; wrong: boolean }
  /** Enter the code SoulThread just sent to the messenger account's own chat (Saved Messages). */
  | { state: 'confirm'; wrong: boolean }
  /** The number already has a SoulThread account: sign in with a text code, then confirm again. */
  | { state: 'sign-in-first'; phoneNumber: string }
  /** `token` is the new session, handed out once, in the answer that finished a sign-in. */
  | { state: 'done'; token?: string }
  | { state: 'failed'; reason: string };

/** A messenger SoulThread keeps in sync for someone, shown in Settings. */
export interface CloudConnection {
  provider: CloudProvider;
  /** The messenger account's own number, which may differ from the SoulThread account's. */
  phoneNumber: string | null;
  status: 'active' | 'disconnected';
  connectedAt: number;
  lastImportAt: number | null;
}

/** Bumped when either side of the hub socket changes shape; the hub accepts this one and the last. */
export const CONNECT_HUB_PROTOCOL = 2;

/** The service's keep-alive, which the hub answers without waking, and the answer. */
export const HUB_PING = '{"type":"ping"}';
export const HUB_PONG = '{"type":"pong"}';

/** From the connect service to the Worker's hub. */
export type ServiceMessage =
  /**
   * `instance` is new each time the service starts, so the hub knows its logins are gone.
   * `providers` are the messengers it can sign in with (protocol 2; protocol 1 meant Telegram).
   */
  | { type: 'hello'; protocol: number; instance: string; providers?: CloudProvider[] }
  | { type: 'qr'; loginId: string; link: string }
  | { type: 'password'; loginId: string; hint: string; wrong: boolean }
  /** `phoneNumber` has its plus, or is empty for an account without a number. */
  | { type: 'authorized'; loginId: string; phoneNumber: string; providerUserId: string }
  | { type: 'failed'; loginId: string; reason: 'expired' | 'error' }
  /** The person ended the session from their phone. */
  | { type: 'revoked'; connectionId: string };

/** From the hub to the connect service. */
export type HubMessage =
  /**
   * The connections to keep; the service logs out of and wipes every other one it holds. And
   * SoulThread's Telegram app credentials, as signed-in Macs get them.
   */
  | {
      type: 'welcome';
      active: string[];
      telegram: { apiId: number; apiHash: string } | null;
    }
  | { type: 'login'; loginId: string; provider: CloudProvider }
  | { type: 'password'; loginId: string; password: string }
  | { type: 'send-code'; loginId: string; code: string }
  | { type: 'cancel'; loginId: string }
  /** Keep the login's session as this connection and sync it with the credential. */
  | {
      type: 'activate';
      loginId: string;
      connectionId: string;
      credential: string;
      accountPhoneNumber: string;
    }
  | { type: 'disconnect'; connectionId: string };
